// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Divider,
  Tooltip,
  IconButton,
  Autocomplete
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CustomAutocomplete from "components/mui/autocomplete";

// ** Icon Imports
import Icon from "components/icon";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";

// ** Toast Imports
import 'react-toastify/dist/ReactToastify.css';

// ** Logic Imports
import { useAddMaterialLogic } from "../logic/AddMaterialLogic";
import { useInventoryPermission } from "../permissions/InventoryPermissions";

// ** Unit Dialog Import
import UnitDialog from "./UnitofMeasurement"; // Adjust the path as needed

const AddMaterial = ({ 
  open, 
  handleClose, 
  handleSave, 
  setLoading, 
  selectedMaterial, 
  materialTypes, 
  onOpenMaterialType
}) => {
  //#region Permissions
  const { InventoryPermissionProps } = useInventoryPermission();
  const {
    hasMaterialSavePermission,
  } = InventoryPermissionProps;
  //#endregion Permissions

  const theme = useTheme();

  // State for Unit Dialog
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [unitCallback, setUnitCallback] = useState(null);

  const {
    materialForm,
    // materialTypes,
    units, 
    errors,
    today,
    handleInputChange,
    handleNumberInputChange,
    handleDateChange,
    handleCheckboxChange,
    handleStatusChange,
    handleSaveMaterial,
    resetForm,
    handleFileChange,
    selectedFile,
    setSelectedFile,
    fetchUnitsData,
    handleUnitCreated
  } = useAddMaterialLogic({ 
    handleClose, 
    setLoading, 
    open, 
    selectedMaterial
    // Remove onOpenUnitDialog from here since we're handling it locally
  });

  // Reset form when dialog opens
  useEffect(() => {
    // Only reset the form if no material is selected (i.e. when adding a new material)
    if (open && !selectedMaterial) {
      resetForm();
    }
  }, [open, selectedMaterial]);

  // Custom save handler to integrate with parent component
  const onSave = async () => {
    if(!hasMaterialSavePermission) return;
    const success = await handleSaveMaterial();
    if (success && handleSave) {
      handleSave();
    }
  };
  
  const dialogTitle = selectedMaterial ? "Edit Material" : "Add New Material";

  // For file upload
  const handleFileInputChange = (e) => {
    if(!hasMaterialSavePermission) return;

    if (e.target.files && e.target.files.length > 0) {
      handleFileChange(e.target.files[0]);
    }
  };

  const handleRemoveAttachment = (e) => {
    if(!hasMaterialSavePermission) return;

    e.stopPropagation();           // don't trigger the picker
    setSelectedFile(null);         // or whatever you call your state setter
  };

  const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined) return '';
    const str = value.toString();

    // Allow typing "." or partial decimals like "0." or ".5"
    if (str === '' || str === '.' || str === '0.' || /^\.\d*$/.test(str)) {
      return str;
    }

    // Remove commas and format integer part
    const parts = str.replace(/,/g, '').split('.');
    if (parts[0] !== '') {
      parts[0] = parseFloat(parts[0]).toLocaleString('en-US');
    }
    return parts.join('.');
  };

  // Handle opening unit dialog
  const handleOpenUnitDialog = (callback) => {
    setUnitCallback(() => callback);
    setUnitDialogOpen(true);
  };

  // Handle unit creation callback
  const handleUnitCreatedCallback = (result) => {
    if (unitCallback) {
      unitCallback(result);
    }
    setUnitDialogOpen(false);
    setUnitCallback(null);
  };

  // Handle unit selection with "Add New" option
  const handleUnitChange = (event, newValue) => {
    if (newValue?.value === '__ADD_NEW_UNIT__') {
      // Open unit dialog
      handleOpenUnitDialog(handleUnitCreated);
    } else {
      // Set the selected unit
      handleInputChange({
        target: {
          name: "unitID",
          value: newValue?.value || ""
        }
      });
    }
  };

  const unitOptions = [
    { value: '__ADD_NEW_UNIT__', label: '+ Create New Unit ', isAddNew: true },
    ...(units || [])
  ];

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {dialogTitle}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <Typography variant="h5">Basic Details</Typography>
              <Divider sx={{ py: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                required
                label="Item Name"
                placeholder="Enter the item name"
                name="itemName"
                value={materialForm.itemName}
                onChange={handleInputChange}
                error={Boolean(errors.itemName)}
                helperText={errors.itemName}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomAutocomplete
                fullWidth
                required
                options={[
                  { value: '__ADD_NEW_MATERIAL__', label: '+ Add New Material Type', isAddNew: true },
                  ...materialTypes
                    .map(type => ({ 
                      value: Number(type.typeCatalogID), 
                      label: type.typeName 
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))
                ]}
                value={
                  materialForm.materialTypeID 
                    ? { 
                        value: materialForm.materialTypeID, 
                        label: materialTypes.find(type => Number(type.typeCatalogID) === materialForm.materialTypeID)?.typeName || '' 
                      }
                    : null
                }
                onChange={(event, newValue) => {
                  if (newValue?.value === '__ADD_NEW_MATERIAL__') {
                    onOpenMaterialType();
                  } else {
                    // Create synthetic event to match your handleInputChange expectations
                    const syntheticEvent = {
                      target: {
                        name: 'materialTypeID',
                        value: newValue?.value || ''
                      }
                    };
                    handleInputChange(syntheticEvent);
                  }
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="Material Type"
                    placeholder="Select Material Type"
                    required
                    error={Boolean(errors.materialTypeID)}
                    helperText={errors.materialTypeID}
                    name="materialTypeID"
                  />
                )}
               renderOption={(props, option) => (
                  <li {...props}>
                    <span style={{ 
                      fontWeight: option.value === '__ADD_NEW_MATERIAL__' ? 'bold' : 'normal',
                    }}>
                      {option.label}
                    </span>
                  </li>
                )}
                PaperProps={{
                  sx: {
                    maxHeight: 200
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label="Material Description"
                placeholder="Enter material description"
                name="description"
                multiline
                rows={3}
                value={materialForm.description}
                onChange={handleInputChange}
                error={Boolean(errors.description)}
                helperText={errors.description}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomAutocomplete
                fullWidth
                options={unitOptions}
                value={
                  unitOptions.find(option => option.value === materialForm.unitID) || null
                }
                onChange={handleUnitChange}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="Unit of Measure"
                    placeholder="Select Unit of Measure"
                    required
                    error={Boolean(errors.unitOfMeasurement)}
                    helperText={errors.unitOfMeasurement}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                   <span style={{
                      fontWeight: option.isAddNew ? 'bold' : 'normal',
                    }}>
                      {option.label}
                    </span>
                  </li>
                )}
                PaperProps={{
                  sx: {
                    maxHeight: 200
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label="Cost Per Unit"
                placeholder="$ 0.00"
                name="costPerUnit"
                value={formatNumberWithCommas(materialForm.costPerUnit)}
                onChange={handleNumberInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label="Per Unit Price"
                placeholder="$ 0.00"
                name="perUnitPrice"
                value={formatNumberWithCommas(materialForm.perUnitPrice)}
                onChange={handleNumberInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel
                sx={{
                  fontWeight: 400,
                  fontSize: "13px",
                  marginBottom: "4px",
                  color: "text.primary",
                  height: "100%",
                }}
              >
                Latest Restock
              </FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: "100%" }}
                  slotProps={{ textField: { size: "small" } }}
                  value={materialForm.latestRestockDate}
                  onChange={(date) => handleDateChange('latestRestockDate', date)}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} mt={4}>
              <Typography variant="h5">Stock Information</Typography>
              <Divider sx={{ py: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label="Quantity in Stock"
                placeholder="Enter quantity in stock"
                name="quantityInStock"
                value={materialForm.quantityInStock}
                onChange={handleNumberInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label="Low Stock Alert"
                placeholder="Enter the low stock alert"
                name="lowStockAlert"
                value={materialForm.lowStockAlert}
                onChange={handleNumberInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label="Location"
                placeholder="Enter the location"
                name="location"
                value={materialForm.location}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                select
                label="Status"
                name="status"
                value={materialForm.isActive ? "Available" : "Unavailable"}
                onChange={handleStatusChange}
              >
                <MenuItem value="Available">Available</MenuItem>
                <MenuItem value="Unavailable">Unavailable</MenuItem>
              </CustomTextField>
            </Grid>
            <Grid item xs={12} mt={4}>
              <Typography variant="h5">Supplier Details</Typography>
              <Divider sx={{ py: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
                <CustomTextField
                fullWidth
                label="Vendor"
                placeholder="Enter the vendors"
                name="vendors"
                value={materialForm.vendors}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel
                sx={{
                  fontWeight: 400,
                  fontSize: "13px",
                  marginBottom: "4px",
                  color: "text.primary",
                }}
              >
                Attachment
              </FormLabel>

              <Box
                component="label"
                htmlFor="upload-material-attachment"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 4,
                  py: 2,
                  gap: 2,
                  border: "1px solid rgba(47, 43, 61, 0.2)",
                  borderRadius: 1,
                  cursor: "pointer",
                }}
              >
                <input
                  accept="image/*,application/pdf"
                  id="upload-material-attachment"
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleFileInputChange}
                />

                {!selectedFile ? (
                  <>
                    <Icon icon="tabler:cloud-upload" />
                    <Typography color="text.secondary">
                      Drop files to attach
                    </Typography>
                  </>
                ) : (
                  <>
                    <Tooltip title={selectedFile.name} arrow placement="top">
                      <span>
                        <Icon
                          icon="tabler:paperclip"
                          fontSize="24"
                          style={{ cursor: "pointer" }}
                        />
                      </span>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={handleRemoveAttachment}
                      sx={{ ml: 0.5 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
          }}
          className="dialog-actions-dense"
        >
          <Box>
            <Button variant="contained" color="info" onClick={handleClose}>
              Cancel
            </Button>
          </Box>
          <Box>
            <Button 
              variant="contained" 
              color="main"
              disabled={!hasMaterialSavePermission}
              {...(hasMaterialSavePermission && {
                onClick: () => onSave()
              })}
            >
              {selectedMaterial ? "Save Changes" : "Save Material"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Unit Dialog */}
      <UnitDialog
        open={unitDialogOpen}
        handleClose={() => setUnitDialogOpen(false)}
        UnitProps={{
          existingUnit: null,
          fetchUnits: fetchUnitsData,
          unitContent: {
            title: "Add Unit of Measurement",
            entity: 'create'
          },
          onUnitCreated: handleUnitCreatedCallback
        }}
      />
    </>
  );
};

export default AddMaterial;